import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const Feedback = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    rating: 0,
    message: "",
  });

  const [hoverRating, setHoverRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRating = (rating) => {
    setFormData((prev) => ({
      ...prev,
      rating,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("Please enter your name.");
      return;
    }

    if (!formData.phone.trim()) {
      toast.error("Please enter your phone number.");
      return;
    }

    if (!/^[0-9]{10}$/.test(formData.phone.trim())) {
      toast.error("Please enter a valid 10-digit phone number.");
      return;
    }

    if (formData.rating === 0) {
      toast.error("Please select a rating.");
      return;
    }

    if (!formData.message.trim()) {
      toast.error("Please enter your feedback.");
      return;
    }

    try {
      setSubmitting(true);

      const response = await api.post("/feedback", {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        message: formData.message.trim(),
        rating: formData.rating,
      });

      if (response.data?.success) {
        toast.success(
          response.data.message ||
            "Thank you! Your feedback has been submitted."
        );

        setFormData({
          name: "",
          phone: "",
          rating: 0,
          message: "",
        });

        setHoverRating(0);
      } else {
        toast.error("Unable to submit feedback. Please try again.");
      }
    } catch (error) {
      console.error("Feedback submission error:", error);

      toast.error(
        error?.response?.data?.detail ||
          "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#145f5b] via-[#1d706b] to-[#427b72] text-white overflow-hidden">
      {/* =====================================================
          HERO / HEADER
      ====================================================== */}

      <section className="relative min-h-screen px-5 sm:px-8 lg:px-12 py-8">

        {/* Decorative glow */}
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-[#c5f135]/10 rounded-full blur-3xl pointer-events-none" />

        <div className="absolute bottom-0 -left-32 w-96 h-96 bg-[#ff4fa3]/10 rounded-full blur-3xl pointer-events-none" />


        {/* =================================================
            MAIN CONTENT
        ================================================== */}

        <div className="relative z-10 max-w-7xl mx-auto pt-14 sm:pt-20">

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">

            {/* =================================================
                LEFT SIDE
            ================================================== */}

            <div className="text-center lg:text-left">

              {/* Small badge */}
              <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/15 border border-white/50 backdrop-blur-sm text-xs sm:text-sm font-bold tracking-wide text-white/80">
                <span className="text-[#c5f135]">✦</span>
                WE VALUE YOUR EXPERIENCE
              </div>

              {/* Heading */}
              <h1 className="mt-7 text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tight">
                Tell Us
                <br />
                <span className="text-[#c5f135] italic">
                  What You Think
                </span>
                <br />
                <span className="text-white">
                  About Your
                </span>
                <br />
                <span className="text-[#ff4fa3]">
                  Experience!!
                </span>
              </h1>

              <p className="mt-7 max-w-xl mx-auto lg:mx-0 text-base sm:text-lg leading-relaxed text-white/80">
                Had a scoop you loved? Or something we could do
                better? We would love to hear from you.
              </p>

              {/* Decorative message */}
              <div className="mt-8 hidden lg:flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-[#c5f135] flex items-center justify-center text-3xl shadow-lg">
                  🍦
                </div>

                <div>
                  <p className="font-black text-lg">
                    Every scoop matters.
                  </p>
                  <p className="text-white/60 text-sm">
                    Your feedback helps us serve you better.
                  </p>
                </div>
              </div>
            </div>

            {/* =================================================
                FEEDBACK FORM
            ================================================== */}

            <div className="w-full max-w-xl mx-auto lg:ml-auto">

              <div className="relative">

                {/* Glow behind card */}
                <div className="absolute inset-0 bg-[#c5f135]/10 blur-3xl rounded-[40px]" />

                <div className="relative bg-[#104f4b]/90 backdrop-blur-xl border border-[#c5f135]/30 rounded-[32px] p-6 sm:p-8 lg:p-10 shadow-2xl">

                  {/* Form heading */}
                  <div className="mb-7">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-2xl bg-[#c5f135] flex items-center justify-center text-2xl">
                        💚
                      </div>

                      <div>
                        <h2 className="text-2xl sm:text-3xl font-black">
                          We'd Love Your Feedback
                        </h2>
                        <p className="text-white/55 text-sm mt-1">
                          Share your experience with us
                        </p>
                      </div>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-5">

                    {/* NAME */}
                    <div>
                      <label className="block text-sm font-bold mb-2 text-white/80">
                        Your Name
                      </label>

                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter your name"
                        className="w-full h-14 rounded-2xl bg-white/10 border border-white/15 px-5 text-white placeholder:text-white/35 outline-none focus:border-[#c5f135] focus:ring-2 focus:ring-[#c5f135]/20 transition"
                      />
                    </div>

                    {/* PHONE */}
                    <div>
                      <label className="block text-sm font-bold mb-2 text-white/80">
                        Phone Number
                      </label>

                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        maxLength={10}
                        placeholder="Enter your 10-digit phone number"
                        className="w-full h-14 rounded-2xl bg-white/10 border border-white/15 px-5 text-white placeholder:text-white/35 outline-none focus:border-[#c5f135] focus:ring-2 focus:ring-[#c5f135]/20 transition"
                      />
                    </div>

                    {/* RATING */}
                    <div>
                      <label className="block text-sm font-bold mb-3 text-white/80">
                        How was your experience?
                      </label>

                      <div className="flex items-center gap-2">
                        {[1, 2, 3, 4, 5].map((star) => {
                          const active =
                            star <= (hoverRating || formData.rating);

                          return (
                            <button
                              key={star}
                              type="button"
                              onClick={() => handleRating(star)}
                              onMouseEnter={() =>
                                setHoverRating(star)
                              }
                              onMouseLeave={() =>
                                setHoverRating(0)
                              }
                              className={`text-4xl sm:text-5xl transition-all duration-200 ${
                                active
                                  ? "text-[#c5f135] scale-110"
                                  : "text-white/20 hover:text-[#c5f135]/60"
                              }`}
                              aria-label={`${star} star rating`}
                            >
                              ★
                            </button>
                          );
                        })}

                        <span className="ml-3 text-sm text-white/50">
                          {formData.rating
                            ? `${formData.rating}/5`
                            : "Select"}
                        </span>
                      </div>
                    </div>

                    {/* MESSAGE */}
                    <div>
                      <label className="block text-sm font-bold mb-2 text-white/80">
                        Feedback / Complaint
                      </label>

                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={5}
                        placeholder="Tell us about your experience..."
                        className="w-full rounded-2xl bg-white/10 border border-white/15 px-5 py-4 text-white placeholder:text-white/35 outline-none resize-none focus:border-[#c5f135] focus:ring-2 focus:ring-[#c5f135]/20 transition"
                      />
                    </div>

                    {/* SUBMIT BUTTON */}
                    <button
                      type="submit"
                      disabled={submitting}
                      className="group w-full h-14 rounded-full bg-[#c5f135] text-[#174d49] font-black text-base sm:text-lg hover:bg-[#d5f64b] hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 shadow-lg disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                    >
                      {submitting ? (
                        <span className="flex items-center justify-center gap-3">
                          <span className="w-5 h-5 border-2 border-[#174d49]/30 border-t-[#174d49] rounded-full animate-spin" />
                          Sending...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          SEND FEEDBACK
                          <span className="text-xl group-hover:translate-x-1 transition-transform">
                            →
                          </span>
                        </span>
                      )}
                    </button>

                    {/* PRIVACY MESSAGE */}
                    <p className="text-center text-xs text-white/35 leading-relaxed">
                      🔒 Your feedback is private and will only be
                      visible to the Ice Cream Paradise team.
                    </p>
                  </form>
                </div>
              </div>
            </div>
          </div>

          {/* =================================================
              BOTTOM MESSAGE
          ================================================== */}

          <div className="mt-14 pb-10 text-center">
            <p className="text-white/60 text-sm sm:text-base">
              🍦 Every scoop. Every smile. Every feedback.
            </p>

            <p className="mt-1 text-[#c5f135] font-bold">
              Helps us make Ice Cream Paradise even better!
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Feedback;